import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { Ref } from 'react';
import { Dialog } from '@myosh/odin-components';
import type { DialogProps, DialogRef, ModalDialogButtonSetting } from '@myosh/odin-components';
import { useTranslation } from 'react-i18next';
import { Subject } from 'rxjs';

export interface ConfirmationDialogProps extends Omit<DialogProps, 'children' | 'buttons' | 'buttonType'> {
  children?: React.ReactNode;
  confirmButtonText?: string;
  closeDialogOnConfirm?: boolean;
  cancelButtonText?: string;
  hasCancelButton?: boolean;
  onConfirm?: () => void;
}

function ConfirmationDialog(props: ConfirmationDialogProps, ref: Ref<DialogRef>) {
  const {
    children,
    confirmButtonText,
    closeDialogOnConfirm,
    cancelButtonText,
    header,
    hasCancelButton,
    visible,
    onClose,
    onConfirm,
    ...rest
  } = props;
  const dialogResultRef = useRef<Subject<{ buttonName: string | undefined }>>(
    new Subject<{ buttonName: string | undefined }>()
  );
  const [isDialogVisible, setIsDialogVisible] = useState(visible ?? false);

  const { t } = useTranslation();

  useEffect(() => {
    if (visible && visible !== isDialogVisible) {
      setIsDialogVisible(visible);
    }
  }, [visible]);

  useImperativeHandle(ref, () => ({
    show: () => setIsDialogVisible(true),
    close: onCloseDialog,
    subscribeToDialogResult: () => dialogResultRef.current.asObservable(),
  }));

  const onCloseDialog = useCallback(
    (returnValue?: string) => {
      setIsDialogVisible(false);
      dialogResultRef.current.next({ buttonName: returnValue });
      if (returnValue !== 'close-icon' && returnValue !== 'overlay') {
        onClose?.(returnValue);
      }
    },
    [onClose]
  );

  const dialogButtons = useMemo(() => {
    const saveButton: ModalDialogButtonSetting = {
      name: 'save',
      text: confirmButtonText ?? t('save'),
      type: 'primary',
      variant: 'alternative',
      closeModalOnClick: closeDialogOnConfirm ?? true,
      onClick: () => {
        onConfirm?.();
      },
    };

    const cancelButton: ModalDialogButtonSetting = {
      name: 'discard',
      text: cancelButtonText ?? t('cancel'),
      variant: 'alternative',
      onClick: () => onCloseDialog('discard'),
    };

    if (hasCancelButton) {
      return [saveButton, cancelButton];
    }

    return [saveButton];
  }, [hasCancelButton, cancelButtonText, confirmButtonText, onConfirm]);

  return (
    <>
      {isDialogVisible && (
        <Dialog
          {...rest}
          buttons={dialogButtons}
          buttonType="button"
          header={header ?? t('save-unsaved-changes')}
          visible={isDialogVisible}
          onClose={onCloseDialog}
        >
          <>{children ?? <p>{t('save-message')}</p>}</>
        </Dialog>
      )}
    </>
  );
}

export default forwardRef(ConfirmationDialog);
